import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Mail, MessageSquarePlus } from 'lucide-react';

const faqs = [
  {
    category: 'Getting Started',
    questions: [
      {
        q: 'What is Flowize?',
        a: 'Flowize is an AI workflow orchestration platform. You describe a task in plain language, and Flowize breaks it into steps, selects the right AI models for each step, and executes the entire pipeline — giving you results, not just prompts.',
      },
      {
        q: 'How do I run my first workflow?',
        a: 'Go to Templates, pick a template that matches your task (Marketing templates are a great start), fill in the input fields, and click "Create Workflow". Flowize will analyse your task, build a workflow graph, and execute it automatically.',
      },
      {
        q: 'Do I need to know how to code?',
        a: 'No. Flowize is designed for non-technical users. You describe what you need in plain language, and the platform handles the rest.',
      },
    ],
  },
  {
    category: 'Billing & Credits',
    questions: [
      {
        q: 'How does pricing work?',
        a: 'Pay As You Go starts at £10 — this includes a £5 monthly platform fee and £5 in credits. Credits are consumed based on the AI models and tools used in your workflows. Additional top-ups are £10+ and go entirely to credits within the same billing period.',
      },
      {
        q: 'What are credits?',
        a: 'Credits are the currency used to pay for AI model usage, tool executions, and compute time within workflows. Different models and tasks consume different amounts of credits based on complexity.',
      },
      {
        q: 'Do unused credits expire?',
        a: 'Purchased credits roll over monthly and expire after 365 days. Included credits on Pro and Team plans expire after 60 days. See our Terms for full details.',
      },
      {
        q: 'Can I get a refund?',
        a: 'Platform fees are non-refundable. Unused purchased credits may be refunded within 14 days if no credits from that purchase have been consumed. Contact billing@flowize.xyz.',
      },
    ],
  },
  {
    category: 'Templates & Workflows',
    questions: [
      {
        q: 'What templates are available?',
        a: 'We offer templates across Marketing, Finance, Operations, Legal, Sales, and more. Marketing templates include Ad Copy Generation, Email Nurture Sequences, Social Media Campaigns, and Landing Page Copy. Browse all templates in the Templates section.',
      },
      {
        q: 'Can I create my own templates?',
        a: 'On Pro plans and above, you can save any successful workflow as a reusable template. Your templates appear in "My Templates" and can be re-run with different inputs.',
      },
      {
        q: 'Why did my workflow pause?',
        a: 'Workflows may pause if they hit a cost ceiling, require approval for a high-cost step, or encounter an error. Check the workflow detail page for the specific reason and options to resume.',
      },
    ],
  },
  {
    category: 'Account & Security',
    questions: [
      {
        q: 'How do I sign in?',
        a: 'Enter your email address on the sign-in page and we\'ll email you a link. There is no password to remember or reset.',
      },
      {
        q: 'Is my data secure?',
        a: 'Yes. All data is encrypted in transit (TLS 1.2+) and stored on EU servers (London). We don\'t use your workflow content to train AI models, and we don\'t use tracking cookies. See our Privacy Policy for full details.',
      },
      {
        q: 'Can I delete my account?',
        a: 'Yes. Contact support@flowize.xyz to request account deletion. Your data will be retained for 30 days after closure, then permanently deleted.',
      },
    ],
  },
];

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left"
      >
        <span className="font-medium text-gray-900 text-sm">{question}</span>
        {open ? <ChevronDown size={16} className="text-gray-400 shrink-0 ml-2" /> : <ChevronRight size={16} className="text-gray-400 shrink-0 ml-2" />}
      </button>
      {open && (
        <p className="pb-4 text-sm text-gray-600 leading-relaxed">{answer}</p>
      )}
    </div>
  );
}

export function HelpPage() {
  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Help & FAQ</h1>
        <p className="text-gray-600 mb-10">
          Common questions about Flowize. Can't find what you need? Get in touch.
        </p>

        {faqs.map((section) => (
          <div key={section.category} className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">{section.category}</h2>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-5">
              {section.questions.map((faq) => (
                <FAQItem key={faq.q} question={faq.q} answer={faq.a} />
              ))}
            </div>
          </div>
        ))}

        {/* Contact section */}
        <div className="mt-12 bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Still need help?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a
              href="mailto:support@flowize.xyz"
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <Mail size={20} className="text-indigo-600" />
              <div>
                <div className="font-medium text-gray-900 text-sm">Email support</div>
                <div className="text-xs text-gray-500">support@flowize.xyz</div>
              </div>
            </a>
            <button
              onClick={() => {
                // Trigger the feedback widget if it exists
                const btn = document.querySelector('[title="Send feedback"]');
                if (btn) btn.click();
              }}
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
            >
              <MessageSquarePlus size={20} className="text-indigo-600" />
              <div>
                <div className="font-medium text-gray-900 text-sm">Send feedback</div>
                <div className="text-xs text-gray-500">Use the feedback widget</div>
              </div>
            </button>
          </div>
        </div>

        {/* Legal links */}
        <div className="mt-8 text-center space-x-6">
          <Link to="/terms" className="text-gray-400 hover:text-gray-600 text-sm">Terms & Conditions</Link>
          <Link to="/privacy" className="text-gray-400 hover:text-gray-600 text-sm">Privacy Policy</Link>
          <Link to="/pricing" className="text-gray-400 hover:text-gray-600 text-sm">Pricing</Link>
        </div>

        <div className="mt-4 text-center">
          <Link to="/" className="text-gray-500 hover:text-gray-700 text-sm">&larr; Back to Flowize</Link>
        </div>
      </div>
    </div>
  );
}
